import { formatListName } from "@/shared/utils/formatListName";
import { quotePhrases } from "@/shared/utils/quotePhrase";
import { Portfolio } from "@/types/Portfolio";
import { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import type { SearchHeadlinesParams } from "@/widget-components/newsquawk-headlines/api/types";
import { resolveSearchDateRange } from "./searchDateWindow";

// Watchlist type detection
export type WatchlistType = "ticker-only" | "keyword-only" | "mixed";

export const getWatchlistType = (portfolio: Portfolio): WatchlistType => {
  const hasTickers = portfolio.tickers.length > 0;
  const hasKeywords = (portfolio.keywords?.length ?? 0) > 0;

  if (hasTickers && !hasKeywords) return "ticker-only";
  if (!hasTickers && hasKeywords) return "keyword-only";
  return "mixed";
};

// Groups portfolios by their type
export const groupPortfoliosByType = (portfolios: Portfolio[]) => {
  return {
    tickerOnly: portfolios.filter((p) => getWatchlistType(p) === "ticker-only"),
    keywordOnly: portfolios.filter((p) => getWatchlistType(p) === "keyword-only"),
    mixed: portfolios.filter((p) => getWatchlistType(p) === "mixed"),
  };
};

// Extracts unique keywords from category queries, filtering out empty values
export const extractKeywordsFromQueries = (
  queries: CategoryQuery[]
): string[] => {
  return queries.map((f) => f.searchKeyword).filter(Boolean) as string[];
};

// Extracts portfolio IDs from category queries as strings
export const extractPortfolioIdsFromQueries = (
  queries: CategoryQuery[]
): string[] => {
  return queries
    .map((f) => f.portfolioIds)
    .flatMap((id) => String(id))
    .filter(Boolean);
};

/**
 * Tickers from a set of watchlists, deduplicated and upper-cased.
 *
 * Upper-casing is not cosmetic. A ticker typed in lower case matched far less
 * than the same ticker typed in capitals, because almost everything downstream
 * compares case-sensitively:
 *
 *   - Newsquawk headlines search: `tickers: ["AAPL"]` returned 40 headlines,
 *     `["aapl"]` returned 4. Someone entering their book in lower case lost
 *     ninety per cent of their coverage silently.
 *   - The equities feeds and the headline matcher build case-sensitive word
 *     boundary regexes and test them against `symbol`, which is capitalised.
 *
 * The firehose is case-insensitive on its ticker field, so it is unaffected
 * either way. Nothing renders this list — every caller is matching or building
 * a request — so the stored value keeps whatever case the user typed.
 */
export const extractTickersFromPortfolios = (
  portfolios: Portfolio[]
): string[] => {
  const allTickers = portfolios.flatMap((p) => p.tickers);
  return Array.from(new Set(allTickers.map((t) => t.toUpperCase())));
};

// Extracts and deduplicates keywords from a list of portfolios
export const extractKeywordsFromPortfolios = (
  portfolios: Portfolio[]
): string[] => {
  const allKeywords = portfolios.flatMap((p) => p.keywords || []);
  return Array.from(new Set(allKeywords));
};

// Builds a formatted search title from selected filters and portfolios, joining with " + "
export const buildSearchTitle = (params: {
  selectedAdvancedFilters: CategoryQuery[];
  selectedFilterPortfolio: Portfolio[];
  selectedSourceLabels?: string[];
  defaultTitle?: string;
}): string => {
  const { selectedAdvancedFilters, selectedFilterPortfolio, selectedSourceLabels, defaultTitle } =
    params;

  const titleParts: string[] = [];

  // Add category filter names
  if (selectedAdvancedFilters && selectedAdvancedFilters.length > 0) {
    const filterNames = selectedAdvancedFilters
      .map((f) => formatListName(f.queryName))
      .join(", ");
    titleParts.push(filterNames);
  }

  // Add portfolio filter names
  if (selectedFilterPortfolio && selectedFilterPortfolio.length > 0) {
    // Sentence-cased on the way out, as everywhere else a list is named —
    // a widget header saying "energy demo watchlist" beside "Gold & Metals
    // Terms" reads as two different systems.
    const selectedPortfolioNames = selectedFilterPortfolio
      .map((p) => formatListName(p.name))
      .join(", ");
    if (selectedPortfolioNames) {
      titleParts.push(selectedPortfolioNames);
    }
  }

  // Add source filter names
  if (selectedSourceLabels && selectedSourceLabels.length > 0) {
    const sourceNames = selectedSourceLabels.join(", ");
    titleParts.push(sourceNames);
  }

  // Build final title
  if (titleParts.length > 0) {
    return `${defaultTitle} (Search: ${titleParts.join(" + ")})`;
  }

  return defaultTitle ?? "Firehose Widget";
};

// Maps portfolio ID lists to portfolio objects, combining and deduplicating IDs from multiple sources
// portfolios parameter should include all portfolios (both user and prebuilt) from the API
export const mapPortfolioIdsToPortfolios = (
  portfolios: Portfolio[],
  ...portfolioIdLists: string[][]
): Portfolio[] => {
  const allPortfolioIds = Array.from(new Set(portfolioIdLists.flat())).map(
    (id) => Number(id)
  );

  const portfolioIdsSet = new Set(allPortfolioIds);

  return portfolios.filter((portfolio) =>
    portfolioIdsSet.has(portfolio.id)
  );
};

// Transforms SearchHeadlinesParams into the API request payload format
export const transformSearchParams = (params: SearchHeadlinesParams) => {
  const {
    query = "",
    rows = 10,
    exactMatch = false,
    advancedFilter,
    portfolios,
    tags = [],
    cursor,
    dateRange,
  } = params;

  const allPortfolios = [
    ...(portfolios || []),
    ...(advancedFilter?.portfolios || []),
  ];

  // Tags a watchlist's terms resolved to are sent alongside its tickers. Safe
  // because filter_mode now defaults to OR: the tag and the text identify the
  // same companies, so they union. Under the old AND default this would have
  // narrowed every list the moment it gained tags.
  const portfolioTagIds = allPortfolios.flatMap((p) => p.tags ?? []);

  const tagIds = [
    ...new Set([
      ...(advancedFilter?.categories || []),
      ...tags,
      ...portfolioTagIds,
    ]),
  ];

  const tickers = extractTickersFromPortfolios(allPortfolios);
  const keywords = extractKeywordsFromPortfolios(allPortfolios);

  // Watchlist keywords are quoted so a phrase stays a phrase; the typed query
  // and the advanced expression are the user's own syntax and are left alone.
  const finalSearchTerm = [
    query,
    ...quotePhrases(keywords),
    advancedFilter?.searchKeyword,
  ]
    .filter(Boolean)
    .join(" OR ");

  const filterMode = advancedFilter?.filterMode || "OR";
  const keywordOp = advancedFilter?.keywordOp || "OR";

  return {
    search_term: finalSearchTerm,
    limit: rows,
    tags: tagIds,
    tickers,
    exact_match: exactMatch,
    filter_mode: filterMode.toUpperCase(),
    keyword_op: keywordOp?.toUpperCase(),
    cursor,
    // Never send an empty date_range: without a start_date the service floors
    // the search at ~30 days. This is the path watchlist/filter searches take,
    // and it never carries a user-chosen date, so every one of them was
    // silently capped. See searchDateWindow.ts — and note `end_date` is dropped
    // server-side regardless of what we send. Remove once the backend stops
    // defaulting.
    date_range: resolveSearchDateRange(dateRange),
  };
};
